import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import HomeNavBar from '../HomePage/HomeNavBar';
import LeftSidebar from '../HomePage/LeftSidebar';
import ConversationListPanel from './ConversationListPanel';
import ChatPanel from './ChatPanel';
import MessageSearchResults from './MessageSearchResults';
import { useComposer } from '../../context/NewMessageComposerContext';
import { authApi } from '../../services/authApi';
import { messageApi, MESSAGES_UPDATED_EVENT, normalizeSearchUser } from '../../services/messageApi';
import { useNavbarUser } from '../../utils/navbarUser';
import './MessagePage.css';

function MessagePage() {
  const [conversations, setConversations] = useState([]);
  const [activeTab, setActiveTab] = useState('messages');
  const [selectedId, setSelectedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isConversationActionPending, setIsConversationActionPending] = useState(false);
  const [error, setError] = useState('');
  const user = useNavbarUser({ notificationCount: 0 });
  const { selectRecipient } = useComposer();
  const [searchParams, setSearchParams] = useSearchParams();
  const lastMarkedReadKeyRef = useRef('');
  const loadRequestIdRef = useRef(0);
  const conversationsRef = useRef([]);

  const conversationIdFromUrl = searchParams.get('conversation');

  const currentUser = useMemo(() => ({
    id: user.id || '',
    name: user.name || 'You',
    avatar: user.avatar || normalizeSearchUser({ username: user.name || 'You' }).avatar,
    spadesBalance: typeof user.spades === 'number' ? user.spades : 0,
  }), [user.avatar, user.id, user.name, user.spades]);

  const selectedConversation = conversations.find((conversation) => conversation.id === selectedId) || null;
  const selectedConversationUpdatedAt = selectedConversation?.updatedAt || null;

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  const mergeConversationSummaries = useCallback((nextConversations, previousConversations) => {
    const previousById = new Map(previousConversations.map((conversation) => [conversation.id, conversation]));

    return nextConversations.map((conversation) => {
      const previousConversation = previousById.get(conversation.id);
      if (!previousConversation) {
        return conversation;
      }

      return {
        ...conversation,
        messages: Array.isArray(previousConversation.messages) ? previousConversation.messages : [],
      };
    });
  }, []);

  const mergeConversationDetail = useCallback((previousConversation, detail) => {
    const nextMessages = Array.isArray(detail?.messages) ? detail.messages : [];
    const lastMessage = nextMessages.length > 0 ? nextMessages[nextMessages.length - 1] : null;

    return {
      ...(previousConversation || {}),
      ...detail,
      lastMessage: lastMessage?.text || previousConversation?.lastMessage || '',
      updatedAt: lastMessage?.timestamp || previousConversation?.updatedAt || null,
      unread: false,
      unreadCount: 0,
    };
  }, []);

  const loadConversationDetail = useCallback(async (conversationId, { silenceErrors = false } = {}) => {
    if (!conversationId) {
      return null;
    }

    try {
      const summary = conversationsRef.current.find((conversation) => conversation.id === conversationId) || null;
      const detail = await messageApi.getConversation(conversationId, summary);

      setConversations((prev) => prev.map((conversation) => (
        conversation.id === conversationId
          ? mergeConversationDetail(conversation, detail)
          : conversation
      )));

      return detail;
    } catch (detailError) {
      if (!silenceErrors) {
        setError(detailError.message || 'Failed to load conversation');
      }
      return null;
    }
  }, [mergeConversationDetail]);

  const loadConversations = useCallback(async () => {
    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;
    setError('');
    try {
      const nextConversations = await messageApi.listConversations();
      if (loadRequestIdRef.current !== requestId) {
        return;
      }

      setConversations((previousConversations) => (
        mergeConversationSummaries(nextConversations, previousConversations)
      ));

      if (!nextConversations.length) {
        setSelectedId(null);
        if (searchParams.get('conversation')) {
          const nextParams = new URLSearchParams(searchParams);
          nextParams.delete('conversation');
          setSearchParams(nextParams, { replace: true });
        }
        return;
      }

      const preferredId = conversationIdFromUrl || selectedId;
      const preferredConversation = preferredId
        ? nextConversations.find((conversation) => conversation.id === preferredId)
        : null;

      if (preferredConversation) {
        setSelectedId(preferredConversation.id);
        setActiveTab(preferredConversation.tabType || 'messages');
      } else {
        setSelectedId(nextConversations[0].id);
        setActiveTab(nextConversations[0].tabType || 'messages');
        if (preferredId) {
          const nextParams = new URLSearchParams(searchParams);
          nextParams.delete('conversation');
          setSearchParams(nextParams, { replace: true });
        }
      }
    } catch (loadError) {
      if (loadRequestIdRef.current === requestId) {
        setError(loadError.message || 'Failed to load conversations');
      }
    } finally {
      if (loadRequestIdRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, [conversationIdFromUrl, mergeConversationSummaries, searchParams, selectedId, setSearchParams]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    const handleMessagesUpdated = () => {
      loadConversations();
    };

    window.addEventListener(MESSAGES_UPDATED_EVENT, handleMessagesUpdated);
    return () => {
      window.removeEventListener(MESSAGES_UPDATED_EVENT, handleMessagesUpdated);
    };
  }, [loadConversations]);

  useEffect(() => {
    const refreshConversations = () => {
      loadConversations();
    };

    const intervalId = window.setInterval(refreshConversations, 15000);
    const handleWindowFocus = () => {
      refreshConversations();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshConversations();
      }
    };

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadConversations]);

  useEffect(() => {
    if (!selectedId) {
      return;
    }

    loadConversationDetail(selectedId);
  }, [loadConversationDetail, selectedConversationUpdatedAt, selectedId]);

  useEffect(() => {
    if (!selectedId) {
      return undefined;
    }

    const refreshSelectedConversation = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      loadConversationDetail(selectedId, { silenceErrors: true });
    };

    const intervalId = window.setInterval(refreshSelectedConversation, 3000);
    const handleWindowFocus = () => {
      refreshSelectedConversation();
    };
    const handleVisibilityChange = () => {
      refreshSelectedConversation();
    };

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadConversationDetail, selectedId]);

  useEffect(() => {
    if (!selectedId) {
      lastMarkedReadKeyRef.current = '';
      return undefined;
    }

    const nextReadKey = `${selectedId}:${selectedConversationUpdatedAt || 'initial'}`;
    if (lastMarkedReadKeyRef.current === nextReadKey) {
      return undefined;
    }

    let cancelled = false;

    const markConversationAsRead = async () => {
      try {
        await messageApi.markRead(selectedId);
        if (cancelled) {
          return;
        }

        lastMarkedReadKeyRef.current = nextReadKey;
        setConversations((prev) => prev.map((conversation) => (
          conversation.id === selectedId
            ? {
                ...conversation,
                unread: false,
                unreadCount: 0,
              }
            : conversation
        )));
      } catch (markReadError) {
        if (!cancelled) {
          console.error('Failed to mark conversation as read:', markReadError);
        }
      }
    };

    markConversationAsRead();

    return () => {
      cancelled = true;
    };
  }, [selectedConversationUpdatedAt, selectedId]);

  useEffect(() => {
    if (selectedConversation?.tabType) {
      setActiveTab(selectedConversation.tabType);
    }
  }, [selectedConversation?.tabType]);

  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchSuggestions([]);
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const results = await authApi.searchUsers(searchQuery);
        if (!cancelled) {
          setSearchSuggestions(results.map(normalizeSearchUser));
        }
      } catch {
        if (!cancelled) {
          setSearchSuggestions([]);
        }
      }
    }, 200);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [searchQuery]);

  const handleSelectConversation = useCallback((id) => {
    const nextConversation = conversations.find((conversation) => conversation.id === id) || null;
    setSelectedId(id);
    setConversations((prev) =>
      prev.map((conversation) => (
        conversation.id === id
          ? { ...conversation, unread: false, unreadCount: 0 }
          : conversation
      ))
    );
    if (nextConversation?.tabType) {
      setActiveTab(nextConversation.tabType);
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('conversation', id);
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
  }, []);

  const handleSendMessage = useCallback(async (text) => {
    if (!selectedId) return;

    setError('');
    try {
      await messageApi.sendMessage(selectedId, text);
      await loadConversations();
      await loadConversationDetail(selectedId, { silenceErrors: true });
    } catch (sendError) {
      setError(sendError.message || 'Failed to send message');
    }
  }, [loadConversationDetail, loadConversations, selectedId]);

  const handleEndChat = useCallback(async () => {
    if (!selectedId) return;

    setIsConversationActionPending(true);
    setError('');
    try {
      await messageApi.endChat(selectedId);
      await loadConversations();
      await loadConversationDetail(selectedId, { silenceErrors: true });
    } catch (actionError) {
      setError(actionError.message || 'Failed to end chat');
    } finally {
      setIsConversationActionPending(false);
    }
  }, [loadConversationDetail, loadConversations, selectedId]);

  const handleDeleteConversation = useCallback(async () => {
    if (!selectedId) return;

    setIsConversationActionPending(true);
    setError('');
    try {
      await messageApi.deleteConversation(selectedId);
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('conversation');
      setSearchParams(nextParams, { replace: true });
      setConversations((prev) => prev.filter((conversation) => conversation.id !== selectedId));
      setSelectedId(null);
      await loadConversations();
    } catch (actionError) {
      setError(actionError.message || 'Failed to delete conversation');
    } finally {
      setIsConversationActionPending(false);
    }
  }, [loadConversations, searchParams, selectedId, setSearchParams]);

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchSuggestions([]);
  }, []);

  const handleStartConversation = useCallback((userSummary) => {
    selectRecipient(userSummary);
    handleClearSearch();
  }, [handleClearSearch, selectRecipient]);

  const isSearching = searchQuery.trim().length > 0;
  const unreadRequestCount = conversations.filter((conversation) => conversation.unread).length;

  return (
    <div className="message-page">
      <HomeNavBar
        spadesBalance={user.spades}
        notificationCount={0}
        userAvatar={user.avatar}
        userName={user.name}
        onSpadesClick={() => {}}
        onLogout={() => {}}
        onSearch={handleSearch}
      />

      <LeftSidebar />

      <div className="message-page__body">
        <ConversationListPanel
          conversations={conversations}
          activeTab={activeTab}
          requestsBadgeCount={unreadRequestCount}
          onTabChange={handleTabChange}
          selectedId={selectedId}
          onSelect={handleSelectConversation}
        />

        {isSearching ? (
          <MessageSearchResults
            query={searchQuery}
            conversations={conversations}
            userSuggestions={searchSuggestions}
            onSelectConversation={handleSelectConversation}
            onStartConversation={handleStartConversation}
            onClearSearch={handleClearSearch}
          />
        ) : (
          <ChatPanel
            conversation={selectedConversation}
            currentUser={currentUser}
            onSendMessage={handleSendMessage}
            onEndChat={handleEndChat}
            onDeleteConversation={handleDeleteConversation}
            isActing={isConversationActionPending}
          />
        )}
      </div>

      {isLoading && (
        <div className="message-page__status">Loading conversations…</div>
      )}
      {!isLoading && error && (
        <div className="message-page__status message-page__status--error">{error}</div>
      )}
    </div>
  );
}

export default MessagePage;
